/**
 * Keyboard input.
 *
 * Milestone 0 needs the system-level keys only (F3 overlay, P pause, Esc). The axis handling
 * — expo curves, rate limits, rebinding, gamepad and touch — arrives with the input milestone;
 * what is established here is the shape: the browser event handlers only ever *record* state,
 * and the simulation reads that state on its own schedule. Nothing simulation-facing happens
 * inside a DOM event, because DOM events do not arrive on the 120 Hz tick.
 *
 * `updateFixed` / `updateFrame`: neither. State is polled by whoever needs it; edge callbacks
 * fire from the browser event.
 */

export type KeyCode = string;

export class Keyboard {
  private readonly down = new Set<KeyCode>();
  private readonly pressHandlers = new Map<KeyCode, Array<() => void>>();
  private readonly target: HTMLElement | Window;
  private attached = false;

  constructor(target: HTMLElement | Window = globalThis.window) {
    this.target = target;
  }

  attach(): void {
    if (this.attached) return;
    this.target.addEventListener('keydown', this.onKeyDown as EventListener);
    this.target.addEventListener('keyup', this.onKeyUp as EventListener);
    globalThis.addEventListener?.('blur', this.onBlur);
    this.attached = true;
  }

  detach(): void {
    if (!this.attached) return;
    this.target.removeEventListener('keydown', this.onKeyDown as EventListener);
    this.target.removeEventListener('keyup', this.onKeyUp as EventListener);
    globalThis.removeEventListener?.('blur', this.onBlur);
    this.attached = false;
  }

  /** Is the key held right now? `code` is a `KeyboardEvent.code`, e.g. `'KeyW'`. */
  isDown(code: KeyCode): boolean {
    return this.down.has(code);
  }

  /** Run `handler` on each fresh press of `code` (auto-repeat is filtered out). */
  onPress(code: KeyCode, handler: () => void): () => void {
    let handlers = this.pressHandlers.get(code);
    if (handlers === undefined) {
      handlers = [];
      this.pressHandlers.set(code, handlers);
    }
    handlers.push(handler);
    return () => {
      const list = this.pressHandlers.get(code);
      if (list === undefined) return;
      const index = list.indexOf(handler);
      if (index >= 0) list.splice(index, 1);
    };
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    this.down.add(event.code);

    const handlers = this.pressHandlers.get(event.code);
    if (handlers !== undefined && handlers.length > 0) {
      // F3 opens the browser's own find bar in some browsers, and F1 opens help — a bound key
      // belongs to the simulator.
      event.preventDefault();
      for (let i = 0; i < handlers.length; i++) handlers[i]();
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.down.delete(event.code);
  };

  /** Losing focus mid-keypress would otherwise leave the key stuck down forever. */
  private readonly onBlur = (): void => {
    this.down.clear();
  };
}
